/**
 * countryCurrency.ts — map a country to its currency (ISO code) + the single
 * currency catalog shared by every picker in the app.
 *
 * The currency converter (ItinerarySection → CurrencyConverter) defaults to
 * source = the venue's local currency (where the band is that day) and
 * target = the band's home currency. A venue may carry its own `currency`
 * (from an online lookup); when empty we fall back to this country→currency
 * map. (2026-08-11; P7 2026-08-21 — corrected orientation: venue SOURCE, band TARGET.)
 *
 * LOT 1 (2026-11-04) — ONE catalog for the whole app: the day-sheet `Currency`
 * select, the converter's target list, the finance selects (`api.CURRENCIES`)
 * and the `currency_note` label all read `CURRENCY_CATALOG` / `CURRENCY_CODES`.
 * Before that, four hand-maintained lists disagreed (10 / 29 / 30 / N codes),
 * so a band touring Finland or Colombia could not even SELECT its currency.
 */

/**
 * Every currency a touring band realistically meets. Order = display order:
 * the day-sheet default (EUR) first, then the home-market currencies, then the
 * rest. `CURRENCY_CODES` is the derived code list the selectors consume.
 */
export const CURRENCY_CATALOG: { code: string; name: string }[] = [
  { code: 'EUR', name: 'Euro' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'USD', name: 'US Dollar' },
  { code: 'GBP', name: 'Pound Sterling' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'PLN', name: 'Polish Zloty' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'RON', name: 'Romanian Leu' },
  { code: 'BGN', name: 'Bulgarian Lev' },
  { code: 'RSD', name: 'Serbian Dinar' },
  { code: 'UAH', name: 'Ukrainian Hryvnia' },
  { code: 'RUB', name: 'Russian Ruble' },
  { code: 'BYN', name: 'Belarusian Ruble' },
  { code: 'GEL', name: 'Georgian Lari' },
  { code: 'MDL', name: 'Moldovan Leu' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'KZT', name: 'Kazakhstani Tenge' },
  { code: 'KGS', name: 'Kyrgyzstani Som' },
  { code: 'AED', name: 'UAE Dirham' },
  { code: 'ILS', name: 'Israeli Shekel' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'COP', name: 'Colombian Peso' },
  { code: 'CLP', name: 'Chilean Peso' },
  { code: 'PEN', name: 'Peruvian Sol' },
  { code: 'BOB', name: 'Bolivian Boliviano' },
  { code: 'CRC', name: 'Costa Rican Colon' },
  { code: 'ARS', name: 'Argentine Peso' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'ISK', name: 'Icelandic Krona' },
  { code: 'ZAR', name: 'South African Rand' },
];

/** ISO codes of the catalog, in display order (derived — never hand-edit). */
export const CURRENCY_CODES: string[] = CURRENCY_CATALOG.map((c) => c.code);

/** True when `code` is a catalog currency (case-insensitive, trimmed). */
export function isKnownCurrency(code?: string | null): boolean {
  return !!code && CURRENCY_CODES.includes(code.trim().toUpperCase());
}

/** Display name of a code, falling back to the code itself. */
export function currencyName(code?: string | null): string {
  if (!code) return '';
  const upper = code.trim().toUpperCase();
  return CURRENCY_CATALOG.find((c) => c.code === upper)?.name || upper;
}

const COUNTRY_TO_CURRENCY: Record<string, string> = {
  // Europe — Euro zone
  Austria: 'EUR',
  Belgium: 'EUR',
  Cyprus: 'EUR',
  Estonia: 'EUR',
  Finland: 'EUR',
  Suomi: 'EUR',
  France: 'EUR',
  Germany: 'EUR',
  Deutschland: 'EUR',
  Greece: 'EUR',
  Ελλάς: 'EUR',
  Ireland: 'EUR',
  Italy: 'EUR',
  Latvia: 'EUR',
  Lithuania: 'EUR',
  Luxembourg: 'EUR',
  Malta: 'EUR',
  Netherlands: 'EUR',
  Portugal: 'EUR',
  Slovakia: 'EUR',
  Slovenia: 'EUR',
  Spain: 'EUR',
  Croatia: 'EUR',
  // Europe — non-euro
  'Czech Republic': 'CZK',
  Czechia: 'CZK',
  Česko: 'CZK',
  Switzerland: 'CHF',
  Schweiz: 'CHF',
  Suisse: 'CHF',
  Svizzera: 'CHF',
  'Schweiz/Suisse/Svizzera/Svizra': 'CHF',
  'United Kingdom': 'GBP',
  UK: 'GBP',
  'Great Britain': 'GBP',
  Poland: 'PLN',
  Sweden: 'SEK',
  Norway: 'NOK',
  Denmark: 'DKK',
  Iceland: 'ISK',
  Hungary: 'HUF',
  Romania: 'RON',
  Turkey: 'TRY',
  Bulgaria: 'BGN',
  Serbia: 'RSD',
  Ukraine: 'UAH',
  Russia: 'RUB',
  'Russian Federation': 'RUB',
  Belarus: 'BYN',
  Moldova: 'MDL',
  // Caucasus / Central Asia — present in `venues`
  Georgia: 'GEL',
  Kazakhstan: 'KZT',
  Kyrgyzstan: 'KGS',
  // North America
  'United States': 'USD',
  USA: 'USD',
  'United States of America': 'USD',
  Canada: 'CAD',
  Mexico: 'MXN',
  // Latin America — present in `venues`
  Brazil: 'BRL',
  Colombia: 'COP',
  Chile: 'CLP',
  Peru: 'PEN',
  Bolivia: 'BOB',
  'Costa Rica': 'CRC',
  Argentina: 'ARS',
  // Asia
  Japan: 'JPY',
  China: 'CNY',
  India: 'INR',
  'South Korea': 'KRW',
  Korea: 'KRW',
  Thailand: 'THB',
  Singapore: 'SGD',
  // Middle East
  'United Arab Emirates': 'AED',
  UAE: 'AED',
  Israel: 'ILS',
  // Africa / Oceania
  'South Africa': 'ZAR',
  Australia: 'AUD',
  'New Zealand': 'NZD',
};

/** Normalize a country string for lookup (lowercase, trim, strip accents). */
function norm(s: string): string {
  return (s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z]/g, '');
}

const NORM_MAP: Record<string, string> = {};
for (const [country, currency] of Object.entries(COUNTRY_TO_CURRENCY)) {
  NORM_MAP[norm(country)] = currency;
}
// The base also stores composite labels (« Schweiz/Suisse/Svizzera/Svizra ») :
// `norm` strips the separators, so the loop above already registers its
// collapsed form — no extra entry needed.

/**
 * Resolve the currency ISO code for a venue country. Returns null when unknown.
 * Matching is accent/case/punctuation-insensitive and handles common aliases
 * (Czechia, Česko, Ελλάς, UK, Suomi, Deutschland…).
 */
export function countryToCurrency(country?: string | null): string | null {
  if (!country) return null;
  const key = norm(country);
  return NORM_MAP[key] ?? null;
}

/**
 * Extract the ISO code a show's `currency_note` carries — the USER'S explicit
 * per-show choice written by the day-sheet `Currency` select as
 * `"SEK — Swedish Krona"`. Only a catalog code counts: a free-text note like
 * `"CET +1 — cash only"` must never be read as a currency.
 */
export function isoFromCurrencyNote(currencyNote?: string | null): string | null {
  if (!currencyNote) return null;
  const m = currencyNote.match(/\b([A-Z]{3})\b/);
  if (!m) return null;
  const code = m[1].toUpperCase();
  return CURRENCY_CODES.includes(code) ? code : null;
}

/**
 * Resolve the best currency for a show, in priority order:
 *   1. the show's own `currency_note` when it carries a catalog ISO code —
 *      a per-show choice BY THE USER always wins (LOT 1 2026-11-04: this used
 *      to come last, so the `<select>` refused to change on a venue show),
 *   2. the venue's own `currency` (from an online country lookup),
 *   3. the country→currency map,
 *   4. null (caller falls back to EUR / the accommodation's country).
 */
export function resolveShowCurrency(
  venueCurrency?: string | null,
  venueCountry?: string | null,
  currencyNote?: string | null,
): string | null {
  const explicit = isoFromCurrencyNote(currencyNote);
  if (explicit) return explicit;
  if (venueCurrency && /^[A-Z]{3}$/.test(venueCurrency.trim())) return venueCurrency.trim().toUpperCase();
  const byCountry = countryToCurrency(venueCountry);
  if (byCountry) return byCountry;
  return null;
}

/**
 * Resolve the band's home currency from its country, falling back to EUR.
 * This is the default currency for expenses/deal, and the currency converter
 * TARGET (the band converts what it spends at the venue/hotel back into its
 * home money). (2026-08-21, P7 — corrected: band is the TARGET, venue SOURCE.)
 */
export function resolveBandCurrency(bandCountry?: string | null): string {
  return countryToCurrency(bandCountry) ?? 'EUR';
}
